const asArray = (value) => (Array.isArray(value) ? value : []);

const asSet = (value) => {
  if (value instanceof Set) return value;
  if (Array.isArray(value)) return new Set(value);
  return new Set();
};

export const normalizeLinkedSubCategoryName = (value) =>
  (typeof value === "string"
    ? value
    : (value?.name ?? value?.value ?? value?.subCategory ?? "")
  )
    ?.toString()
    ?.toLowerCase()
    ?.trim() ?? "";

export const parseLinkedSubCategories = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    return raw
      .split(",")
      .map((item) => item?.trim())
      .filter(Boolean);
  }
  if (raw && typeof raw === "object") {
    return [raw];
  }
  return [];
};

export const getLinkedSubCategorySet = (service) => {
  const raw =
    service?.linkedSubcategories ?? service?.linkedSubCategories ?? [];
  const list = parseLinkedSubCategories(raw);
  return new Set(
    asArray(list).map(normalizeLinkedSubCategoryName).filter(Boolean),
  );
};

export const getStockByIdMap = (clinicStocks = []) => {
  const map = new Map();
  asArray(clinicStocks).forEach((stock) => {
    if (stock?.stockId != null) {
      map.set(stock.stockId, stock);
    }
  });
  return map;
};

export const resolveServiceVariant = (service, variantId, clinicId) => {
  if (!service) return null;

  const variants = asArray(service?.serviceVariants)?.filter((vr) =>
    vr?.createdBy ? vr?.createdBy === clinicId : true,
  );
  if (!variants?.length) return null;

  if (variantId != null) {
    const byId = variants?.find(
      (vnt) => String(vnt?.variantId) === String(variantId),
    );
    if (byId) return byId;
  }

  return variants?.[0] ?? null;
};

export const filterScopedInventoryList = ({
  baseList = [],
  searchQuery = "",
  matchesKeywordSearch,
  variantStockIds,
  existingStockIds,
  suggestedStockIds,
  limit = 100,
} = {}) => {
  const variantIds = asSet(variantStockIds);
  const existingIds = asSet(existingStockIds);
  const suggestedIds = asSet(suggestedStockIds);

  const filtered = asArray(baseList).filter((item) => {
    if (!item?.stockId) return false;
    if (variantIds.has(item.stockId)) return false;
    if (existingIds.has(item.stockId)) return false;
    if (suggestedIds.has(item.stockId)) return false;
    if (
      typeof matchesKeywordSearch === "function" &&
      !matchesKeywordSearch(item, searchQuery)
    ) {
      return false;
    }
    return true;
  });

  return filtered.slice(0, Number(limit) > 0 ? Number(limit) : 100);
};

export const buildDisplaySuggestedItems = ({
  variantSuggestedItems = [],
  selectedItems = [],
  addedItems = [],
} = {}) => {
  const variants = asArray(variantSuggestedItems);
  const selected = asArray(selectedItems);
  const added = asArray(addedItems);

  const addedByStockId = new Map(
    added.map((item) => [item?.stockId, item]).filter(([id]) => id != null),
  );
  const selectedByStockId = new Map(
    selected.map((item) => [item?.stockId, item]).filter(([id]) => id != null),
  );

  const variantRows = variants.map((variantItem) => {
    const addedMatch = addedByStockId.get(variantItem?.stockId);
    if (addedMatch) {
      return {
        ...variantItem,
        ...addedMatch,
        alreadyAdded: Boolean(addedMatch?.alreadyAdded),
        fromVariant: variantItem?.fromVariant ?? true,
      };
    }
    const selectedMatch = selectedByStockId.get(variantItem?.stockId);
    if (selectedMatch) {
      return {
        ...variantItem,
        ...selectedMatch,
        alreadyAdded: false,
        newlySelected: true,
        fromVariant: variantItem?.fromVariant ?? true,
      };
    }
    return {
      ...variantItem,
      alreadyAdded: false,
      fromVariant: variantItem?.fromVariant ?? true,
    };
  });

  const suggestedStockIdSet = new Set(
    variants.map((item) => item?.stockId).filter(Boolean),
  );
  const otherAddedItems = added.filter(
    (item) => item?.stockId && !suggestedStockIdSet.has(item.stockId),
  );

  return [...variantRows, ...otherAddedItems].filter(Boolean);
};

export const isVariantBomLine = (li) => {
  if (!li || typeof li !== "object") return false;
  return (
    (li?.alterType === "N" && !li?.alterName) ||
    (li?.alterType === "Y" && !li?.alterName) ||
    (li?.alterType == null && !li?.alterName && li?.stockId)
  );
};

export const getVariantBomLines = (serviceVariant, itemType) => {
  if (!serviceVariant) return [];
  const isAdmin = String(itemType ?? "")
    .toLowerCase()
    .startsWith("admin");
  const lines = isAdmin
    ? asArray(serviceVariant?.presList).length
      ? serviceVariant.presList
      : asArray(serviceVariant?.servicePrescreiptions).length
        ? serviceVariant.servicePrescreiptions
        : serviceVariant?.servicePrescription
    : asArray(serviceVariant?.consumList).length
      ? serviceVariant.consumList
      : serviceVariant?.serviceDetails;
  return asArray(lines).filter(isVariantBomLine);
};

export const getLinkedSubCategoryItems = ({
  baseList = [],
  linkedSubCategorySet,
  stockByIdMap,
  excludeStockIds,
} = {}) => {
  const linkedSet = asSet(linkedSubCategorySet);
  if (!linkedSet.size) return [];
  const exclude = asSet(excludeStockIds);
  const map = stockByIdMap instanceof Map ? stockByIdMap : new Map();
  return asArray(baseList).filter((item) => {
    if (!item?.stockId || exclude.has(item.stockId)) return false;
    const stock = map.get(item.stockId);
    if (!stock) return false;
    return linkedSet.has(normalizeLinkedSubCategoryName(stock?.subCategory));
  });
};

export const getWorkbookItemActualCost = ({
  item,
  selectedBatch,
  quantity,
  dosage,
} = {}) => {
  const qty = Number(quantity ?? item?.quantity ?? 0);
  const dosageNum = Number(dosage ?? item?.dosage ?? 0);
  const unitSellingPrice = Number(selectedBatch?.unitSellingPrice ?? 0);
  if (selectedBatch && unitSellingPrice) {
    const packMatch = selectedBatch?.packageUom?.match(/^(\d+)(.+)/);
    const packQty = Number(packMatch?.[1] ?? 0);
    if (dosageNum > 0 && packQty > 0) {
      return unitSellingPrice * (dosageNum / packQty);
    }
    return unitSellingPrice * (qty || 1);
  }
  return Number(item?.estimatedCost ?? item?.estCost ?? 0);
};

export const shouldShowWorkbookItemPrice = (item = {}) => {
  const isBillSeparated = !!(item?.billSeparated || item?.billedSeparately);
  const isLinked = item?.linked === "Y" || !!item?.serviceId;
  return isBillSeparated || !isLinked;
};

export const getWorkbookItemDisplayCost = (item = {}, actualCost = 0) =>
  shouldShowWorkbookItemPrice(item) ? Number(actualCost ?? 0) : 0;
